import {
  ConfusionMatrixViz,
  CorrelationsViz,
  DataQualityViz,
  FeatureInteractionViz,
  LiftGainViz,
  MissingViz,
  OverviewViz,
  QuickFitViz,
  ResidualAnalysisViz,
  RocPrCalibrationViz,
  TargetViz,
} from "@/features/eda/visualizations";
import { Component, type ErrorInfo, type ReactNode } from "react";
import type { AnalysisResultMap } from "./types";

export interface ResultRendererProps {
  analyses: Record<string, unknown>;
}

class VizErrorBoundary extends Component<
  { kind: string; children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn(`[EDA] ${this.props.kind} viz failed:`, error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <p className="font-mono text-[11px] font-semibold text-amber-800 dark:text-amber-200">
            Could not render "{this.props.kind}"
          </p>
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
            {this.state.error.message}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

export function ResultRenderer({ analyses }: ResultRendererProps) {
  return (
    <div className="space-y-6">
      {Object.entries(analyses).map(([kind, data]) => (
        <VizErrorBoundary key={kind} kind={kind}>
          {renderViz(kind, data)}
        </VizErrorBoundary>
      ))}
    </div>
  );
}

function renderViz(kind: string, d: unknown): ReactNode {
  switch (kind) {
    case "overview":
      return <OverviewViz data={d as AnalysisResultMap["overview"]} />;
    case "missing":
      return <MissingViz data={d as AnalysisResultMap["missing"]} />;
    case "target":
      return <TargetViz data={d as AnalysisResultMap["target"]} />;
    case "correlations":
    case "correlation_significance":
      return <CorrelationsViz data={d as AnalysisResultMap["correlations"]} />;
    case "quick_fit":
      return <QuickFitViz data={d as AnalysisResultMap["quick_fit"]} />;
    case "feature_interaction":
      return <FeatureInteractionViz data={d as AnalysisResultMap["feature_interaction"]} />;
    case "target_leakage":
    case "class_imbalance":
    case "label_noise":
      return <DataQualityViz kind={kind} data={d as AnalysisResultMap["target_leakage"]} />;
    case "confusion_matrix":
      return <ConfusionMatrixViz data={d as AnalysisResultMap["confusion_matrix"]} />;
    case "roc_pr_calibration_curves":
      return <RocPrCalibrationViz data={d as AnalysisResultMap["roc_pr_calibration_curves"]} />;
    case "residual_analysis":
      return <ResidualAnalysisViz data={d as AnalysisResultMap["residual_analysis"]} />;
    case "lift_gain_chart":
      return <LiftGainViz data={d as AnalysisResultMap["lift_gain_chart"]} />;
    default:
      return (
        <pre className="overflow-auto rounded-md bg-code-bg p-3 font-mono text-xs text-ink">
          {JSON.stringify(d, null, 2)}
        </pre>
      );
  }
}
